import type React from 'react';
import type { BankDayState } from '@/api/reconciliation';
import { cn } from '@/lib/utils';

/**
 * Plain-language labels for the three per-day delivery states.
 *
 * The three state values are the endpoint's contract; the wording here is the
 * screen's choice. These read as an operator would say them rather than as the
 * pipeline describes them, which is why "not_delivered" is "Not received" and not
 * "Missing": on a quiet day nothing is wrong, and the word should not imply it is.
 */
const STATE_LABEL: Record<BankDayState, string> = {
  present: 'Received',
  not_delivered: 'Not received',
  delivered_not_parsed: 'Could not read',
};

const STATE_DESCRIPTION: Record<BankDayState, string> = {
  present: "The bank's file for this day arrived and was read",
  not_delivered: 'No file arrived for this day by the time one was expected',
  delivered_not_parsed: 'A file arrived for this day but could not be read',
};

const STATE_CLASS: Record<BankDayState, string> = {
  present: 'bg-matched/10 text-matched',
  not_delivered: 'bg-muted text-muted-foreground',
  delivered_not_parsed: 'bg-debit/10 text-debit',
};

export const BankDayStateBadge: React.FC<{ state: BankDayState; className?: string }> = ({
  state,
  className,
}) => (
  <span
    className={cn(
      'shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium',
      STATE_CLASS[state],
      className
    )}
    title={STATE_DESCRIPTION[state]}
  >
    {STATE_LABEL[state]}
  </span>
);
